import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Dialog } from "../../../components/ui/dialog";
import { Input, Label } from "../../../components/ui/input";
import { Table, TBody, Td, THead, Th, Tr } from "../../../components/ui/table";
import { api, qk, unwrap } from "../../../lib/api";
import { toastError } from "../../../lib/hooks";
import { formatBytes, formatDate } from "../../../lib/utils";

export const Route = createFileRoute("/_app/admin/")({ component: AdminOrgs });

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);

function AdminOrgs() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState<string | null>(null);
  const orgs = useQuery({ queryKey: qk.admin.orgs, queryFn: () => unwrap(api.admin.orgs.$get()) });
  const refresh = () => qc.invalidateQueries({ queryKey: qk.admin.orgs });

  const create = useMutation({
    mutationFn: (v: { name: string; slug: string; ownerEmail: string }) =>
      unwrap(api.admin.orgs.$post({ json: v })),
    onSuccess: async (r) => {
      setCreating(false);
      setName("");
      setSlug(null);
      toast.success(`Created. Invitation sent to ${r.invitation.email}`);
      await refresh();
    },
    onError: toastError,
  });
  const action = useMutation({
    mutationFn: async ({
      orgId,
      op,
    }: {
      orgId: string;
      op: "suspend" | "unsuspend" | "delete" | "invite";
    }) => {
      if (op === "suspend")
        return unwrap(api.admin.orgs[":orgId"].suspend.$post({ param: { orgId } }));
      if (op === "unsuspend")
        return unwrap(api.admin.orgs[":orgId"].unsuspend.$post({ param: { orgId } }));
      if (op === "delete") return unwrap(api.admin.orgs[":orgId"].$delete({ param: { orgId } }));
      const email = prompt("Owner email to invite:");
      if (email)
        return unwrap(
          api.admin.orgs[":orgId"]["invite-owner"].$post({ param: { orgId }, json: { email } }),
        );
    },
    onSuccess: refresh,
    onError: toastError,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)}>
          <Plus /> New organization
        </Button>
      </div>
      <Table>
        <THead>
          <Tr>
            <Th>Organization</Th>
            <Th>Members</Th>
            <Th>Items</Th>
            <Th>Storage</Th>
            <Th>Created</Th>
            <Th />
          </Tr>
        </THead>
        <TBody>
          {orgs.data?.orgs.map((o) => (
            <Tr key={o.id}>
              <Td>
                <span className="font-medium">{o.name}</span>{" "}
                <span className="text-xs text-muted-foreground">{o.slug}</span>{" "}
                {o.suspendedAt && <Badge variant="destructive">suspended</Badge>}
              </Td>
              <Td>{o.memberCount}</Td>
              <Td>{o.itemCount}</Td>
              <Td>{formatBytes(o.storageBytes)}</Td>
              <Td className="text-muted-foreground">{formatDate(o.createdAt)}</Td>
              <Td className="space-x-1 text-right whitespace-nowrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => action.mutate({ orgId: o.id, op: "invite" })}
                >
                  Invite owner
                </Button>
                {o.suspendedAt ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => action.mutate({ orgId: o.id, op: "unsuspend" })}
                    >
                      Unsuspend
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        prompt(
                          `Type "${o.slug}" to permanently delete this organization and all its data:`,
                        ) === o.slug && action.mutate({ orgId: o.id, op: "delete" })
                      }
                    >
                      Delete
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => action.mutate({ orgId: o.id, op: "suspend" })}
                  >
                    Suspend
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      <Dialog open={creating} onClose={() => setCreating(false)} title="New organization">
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            create.mutate({
              name,
              slug: slug ?? slugify(name),
              ownerEmail: String(f.get("ownerEmail")),
            });
          }}
        >
          <Label htmlFor="o-name">Name</Label>
          <Input id="o-name" value={name} onChange={(e) => setName(e.target.value)} required />
          <Label htmlFor="o-slug">Slug</Label>
          <Input
            id="o-slug"
            value={slug ?? slugify(name)}
            onChange={(e) => setSlug(e.target.value)}
            pattern="[a-z0-9-]+"
            required
          />
          <Label htmlFor="o-owner">Owner email (gets an invitation)</Label>
          <Input id="o-owner" name="ownerEmail" type="email" required />
          <Button type="submit" disabled={create.isPending}>
            Create and invite owner
          </Button>
        </form>
      </Dialog>
    </div>
  );
}
